import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import IconButton from "../button/IconButton";
import { grey } from "designSystem/styles/color";
import GridItemGrow from "../grid/GridItemGrow";
import CenteredArrowBackIosIcon from "../icon/CenteredArrowBackIosIcon";
import { HOVER } from "designSystem/theme";
import ColorChip from "./ColorChip";
import { ColorSpec, useColorChipArrayStore } from "./ColorChipArrayStore";

export interface ColorChipArrayInternalCallbackProps {
	/** callback for when a color chip is hovered */
	onMouseEnterChip?: (chip: ColorSpec) => void;

	/** callback for when a color chip is no longer hovered */
	onMouseLeaveChip?: (chip?: ColorSpec) => void;

	/** callback for when a color chip is selected */
	onSelectChip?: (chip: ColorSpec) => void;
}

export interface ColorChipArrayInternalProps extends ColorChipArrayInternalCallbackProps {
}

const chipSelected = "ColorChipArraySelected";
const StyledIconButton = styled(IconButton)(({
	theme,
}) => ({
	borderColor: "transparent",
	backgroundColor: "transparent",
	borderRadius: 5,
	height: 24,
	padding: 0,
	width: 24,

	[HOVER]: {
		backgroundColor: "transparent",
		borderColor: grey[400],
	},

	[`&.${chipSelected}`]: {
		borderColor: `${theme.palette.FocusSecondary} !important`,
	},
}));

export const ColorChipArrayInternal: FunctionComponent<ColorChipArrayInternalProps> = observer((props) => {
	const {
		onSelectChip,
		onMouseEnterChip,
		onMouseLeaveChip,
	} = props;
	const {
		canNextPage,
		canPrevPage,
		chipsWidth,
		displayedColorList,
		hasMultiplePages,
		isDefaultColor,
		isSelected,
		nextPage,
		prevPage,
		setValue,
	} = useColorChipArrayStore();

	const onClickChip = (item: ColorSpec) => {
		setValue(item);
		onSelectChip?.(item);
	};

	/**
	 * When switching pages and re-rendering, we effectively
	 * want to stop mousing over any chip, so call this
	 */
	onMouseLeaveChip?.();

	const gridItems = displayedColorList.map((item, i) => (
		<Grid item key={"colorchip_grid_item_" + i}>
			<StyledIconButton
				className={isSelected(item) ? chipSelected : ""}
				onClick={() => onClickChip(item)}
				onMouseEnter={() => onMouseEnterChip?.(item)}
				onMouseLeave={() => onMouseLeaveChip?.(item)}
				size="large"
				title={item.name}
			>
				<ColorChip
					hasViolation={item.hasViolation}
					isDefault={isDefaultColor(item)}
					src={item.image}
				/>
			</StyledIconButton>
		</Grid>
	));

	return (
		<Grid
			alignItems="center"
			container
			direction="row"
			spacing={1}
			wrap="nowrap"
		>
			{hasMultiplePages && (
				<Grid item>
					<IconButton
						disabled={!canPrevPage}
						onClick={prevPage}
						size="small"
					>
						<CenteredArrowBackIosIcon />
					</IconButton>
				</Grid>
			)}
			<GridItemGrow
				sx={{
					display: "flex",
					justifyContent: "center",
				}}
			>
				<Grid
					alignItems="center"
					container
					direction="row"
					spacing={1}
					style={hasMultiplePages ? { width: chipsWidth } : null}
				>
					{gridItems}
				</Grid>
			</GridItemGrow>
			{hasMultiplePages && (
				<Grid item>
					<IconButton
						disabled={!canNextPage}
						onClick={nextPage}
						size="small"
					>
						<ArrowForwardIosIcon />
					</IconButton>
				</Grid>
			)}
		</Grid>
	);
});

export default ColorChipArrayInternal;
